import { getColorMode, type PublicUserConfig } from '@ordo/auth'
import { useEffect } from 'react'
import { create } from 'zustand'

import { apiFetch } from '@/api/http'
import { applyDefaultThemePreference } from '@/lib/theme'

/**
 * Set when the current tab was entered via Customers' "Access school/chef
 * view" or "View as Parent" (act-on-behalf-of). Drives the take-over banner
 * in the app shell.
 */
export interface ImpersonationContext {
  /** What the operator is viewing as; absent on older tokens means 'org'. */
  kind?: 'org' | 'household'
  orgName: string
  impersonatorEmail: string | null
  startedAt: string
  /** A guest's view-only visit — the server refuses every change. Absent on older servers. */
  readOnly?: boolean
}

export interface AuthUser {
  id: string
  email: string
  name?: string
  active: boolean
  /** Per-user config from multipass (color mode, …). GET /api/auth/me always sends it. */
  config: PublicUserConfig
  impersonation?: ImpersonationContext | null
}

interface AuthState {
  user: AuthUser | null
  status: 'loading' | 'idle'
  setUser: (user: AuthUser | null) => void
  setStatus: (status: AuthState['status']) => void
  refresh: () => Promise<void>
  signOut: () => Promise<void>
  dismissedLoginPrompts: Array<'password' | 'chefDocuments'>
  dismissLoginPrompt: (prompt: 'password' | 'chefDocuments') => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'loading',
  dismissedLoginPrompts: [],
  dismissLoginPrompt: (prompt) =>
    set((state) => ({ dismissedLoginPrompts: [...new Set([...state.dismissedLoginPrompts, prompt])] })),
  setUser: (user) =>
    set((state) => ({
      user,
      status: 'idle',
      // Keep dismissals across route changes and auth refreshes, but ask again
      // after sign-out or when a different user signs in. Never persist them.
      dismissedLoginPrompts: state.user?.id === user?.id ? state.dismissedLoginPrompts : [],
    })),
  setStatus: (status) => set({ status }),
  refresh: async () => {
    set({ status: 'loading' })
    try {
      // Route through apiFetch so the per-tab impersonation headers are sent —
      // otherwise /me resolves the operator's own session, not the view.
      const user = await apiFetch<AuthUser>('/api/auth/me')
      get().setUser(user)
      // Seed this device from the account-level preference (PROD-986). A
      // local pick already in localStorage wins — see applyDefaultThemePreference.
      applyDefaultThemePreference(getColorMode(user))
    } catch {
      get().setUser(null)
    }
  },
  signOut: async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    get().setUser(null)
  },
}))

let bootstrapped = false

export function useAuth() {
  const state = useAuthStore()
  useEffect(() => {
    if (!bootstrapped) {
      bootstrapped = true
      void state.refresh()
    }
  }, [state])
  return state
}
